import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface QueueItem {
  id: string;
  prompt: string;
  model: string;
  totalImages: number;
  processedImages: number;
  status: 'pending' | 'processing' | 'completed' | 'error' | 'paused';
  generatedImages: Array<{
    id?: string;
    url: string;
    b64_json?: string;
    revised_prompt?: string;
  }>;
  enableEnhancedPipeline?: boolean;
  error?: string;
  size: string;
  quality: string;
  style: string;
  background?: string;
  outputFormat?: string;
  outputCompression?: number;
  moderation?: string;
  createdAt: number;
  startedAt?: number;
  completedAt?: number;
  priority: number;
  retryCount: number;
  batchSize: number;
  userRequestedCount: number;
  lastDisplayedCount: number;
  displayedBatches: number;
  abTestVariantId?: string;
}

interface RateLimitInfo {
  requestsThisMinute: number;
  resetTime: number;
}

// Model-specific prompt limits and timeouts
const getModelPromptLimit = (modelKey: string): number => {
  if (!modelKey) return 2000;
  
  const lowerModel = modelKey.toLowerCase();
  
  if (lowerModel.includes('dall-e-3') || lowerModel.includes('gpt-image')) {
    return 4000;
  }
  
  if (lowerModel.includes('dall-e-2')) {
    return 1000;
  }
  
  return 2000;
};

// Model-specific timeout limits (in milliseconds)
const getModelTimeout = (modelKey: string): number => {
  if (!modelKey) return 120000; // 2 minutes default
  
  const lowerModel = modelKey.toLowerCase();
  
  // GPT Image models get 3 minutes timeout
  if (lowerModel.includes('gpt-image') || lowerModel.includes('gpt_image')) {
    return 180000; // 3 minutes
  }
  
  // All other models get 2 minutes
  return 120000; // 2 minutes
};

// Get model batch size from admin configuration
const getModelBatchSize = async (modelKey: string): Promise<number> => {
  try {
    console.log('🔍 Fetching batch size for model:', modelKey);
    const { data, error } = await supabase
      .from('model_configs')
      .select('max_batch_size')
      .eq('model_key', modelKey)
      .eq('is_active', true)
      .single();
      
    if (!error && data?.max_batch_size) {
      console.log('✅ Found admin-configured batch size:', data.max_batch_size, 'for model:', modelKey);
      return data.max_batch_size;
    }
    
    console.log('⚠️ No batch size found for model:', modelKey, 'using default');
  } catch (error) {
    console.log('❌ Error fetching model batch size:', error);
  }
  
  // Use default batch size of 4 if no admin configuration found
  // This matches the default in AdminModelManagement
  console.log(`📊 No admin config found for ${modelKey}, using default batch size: 4`);
  return 4;
};

// Persistent storage functions
const getStorageKey = (userId: string) => `imageQueue_${userId}`;

const saveQueueToStorage = (userId: string, queue: QueueItem[]) => {
  try {
    // Only store essential queue data, exclude large image data to prevent quota exceeded
    const serializedQueue = queue.map(item => {
      // Persist only a small, safe subset of generated image URLs to avoid storage bloat
      const generatedImageUrls = (item.generatedImages || [])
        .map(img => img.url)
        .filter(Boolean)
        .slice(0, 6); // cap per item

      return {
        id: item.id,
        prompt: item.prompt,
        model: item.model,
        size: item.size,
        quality: item.quality,
        style: item.style,
        background: item.background,
        outputFormat: item.outputFormat,
        status: item.status,
        error: item.error,
        createdAt: item.createdAt,
        processedImages: item.processedImages || 0,
        totalImages: item.totalImages || 1,
        retryCount: item.retryCount || 0,
        generatedImageCount: item.generatedImages?.length || 0,
        generatedImageUrls
      };
    });
    
    const dataToStore = JSON.stringify(serializedQueue);
    // Check size before storing (5MB limit for localStorage)
    if (dataToStore.length > 5000000) {
      console.warn('Queue data too large for localStorage, keeping only recent items');
      // Keep only last 10 items
      const reducedQueue = serializedQueue.slice(-10);
      localStorage.setItem(getStorageKey(userId), JSON.stringify(reducedQueue));
      console.log('💾 Queue reduced and saved to localStorage:', reducedQueue.length, 'items');
    } else {
      localStorage.setItem(getStorageKey(userId), dataToStore);
      console.log('💾 Queue saved to localStorage:', serializedQueue.length, 'items');
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, clearing old queue data');
      try {
        localStorage.removeItem(getStorageKey(userId));
        // Try to save only the most recent items without image data
        const recentItems = queue.slice(-5).map(item => ({
          id: item.id,
          status: item.status,
          error: item.error,
          prompt: item.prompt.substring(0, 100) // Truncate long prompts
        }));
        localStorage.setItem(getStorageKey(userId), JSON.stringify(recentItems));
        console.log('💾 Saved reduced queue after quota exceeded:', recentItems.length, 'items');
      } catch (e) {
        console.warn('Could not save even reduced queue data:', e);
        // Clear everything if we still can't save
        localStorage.removeItem(getStorageKey(userId));
      }
    } else {
      console.warn('Failed to save queue to localStorage:', error);
    }
  }
};

const loadQueueFromStorage = (userId: string): QueueItem[] => {
  try {
    const stored = localStorage.getItem(getStorageKey(userId));
    if (stored) {
      const parsedQueue = JSON.parse(stored);
      // Filter out completed or error items older than 24 hours
      const cutoffTime = Date.now() - (24 * 60 * 60 * 1000);
      return parsedQueue.filter((item: any) => {
        if (item.status === 'completed' || item.status === 'error') {
          return (item.completedAt || item.createdAt) > cutoffTime;
        }
        return true;
      }).map((item: any) => ({
        ...item,
        // Restore a small set of generated images from persisted URLs (if any)
        generatedImages: Array.isArray(item.generatedImageUrls)
          ? item.generatedImageUrls.slice(0, 6).map((url: string) => ({ url }))
          : (item.generatedImages || []),
        processedImages: item.processedImages || 0,
        totalImages: item.totalImages || 1,
        userRequestedCount: item.userRequestedCount || 1,
        lastDisplayedCount: item.lastDisplayedCount || 0,
        displayedBatches: item.displayedBatches || 0
      }));
    }
  } catch (error) {
    console.warn('Failed to load queue from localStorage:', error);
  }
  return [];
};

const clearStorageQueue = (userId: string) => {
  try {
    localStorage.removeItem(getStorageKey(userId));
    console.log('🗑️ Queue cleared from localStorage');
  } catch (error) {
    console.warn('Failed to clear queue from localStorage:', error);
  }
};

export const useImageQueue = (userId?: string) => {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo>({
    requestsThisMinute: 0,
    resetTime: Date.now() + 60000
  });
  
  const processingRef = useRef(false);
  const rateLimitRef = useRef({ count: 0, resetTime: Date.now() + 60000 });
  const lastRequestTime = useRef(0);
  const processQueueTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeProcessingId = useRef<string | null>(null);
  const pausedItems = useRef<Set<string>>(new Set());

  // Load queue from storage on mount and restore generated images from database
  useEffect(() => {
    if (userId) {
      const restoreQueue = async () => {
        const storedQueue = loadQueueFromStorage(userId);
        if (storedQueue.length > 0) {
          console.log('📂 Loaded queue from storage:', storedQueue.length, 'items');
          
          // Fetch actual generated images from database for each queue item
          const restoredQueue = await Promise.all(
            storedQueue.map(async (item) => {
              try {
                const { data: images, error } = await supabase
                  .from('generated_images')
                  .select('image_url, b64_json, prompt')
                  .eq('batch_id', item.id)
                  .eq('user_id', userId)
                  .order('created_at', { ascending: true });

                if (!error && images && images.length > 0) {
                  console.log(`✅ Restored ${images.length} images for queue item ${item.id}`);
                  return {
                    ...item,
                    generatedImages: images.map(img => ({
                      url: img.image_url,
                      b64_json: img.b64_json,
                      revised_prompt: img.prompt
                    })),
                    processedImages: images.length,
                    // Mark as completed if all images are generated
                    status: images.length >= item.totalImages ? 'completed' : item.status
                  };
                }
              } catch (error) {
                console.error('Error restoring images for queue item:', item.id, error);
              }
              return item;
            })
          );
          
          setQueue(restoredQueue);
          console.log('🔄 Queue fully restored with generated images');
        }
      };
      
      restoreQueue();
    }
  }, [userId]);

  // Save queue to storage whenever it changes
  useEffect(() => {
    if (userId && queue.length >= 0) { // Save even when empty to persist deletions
      saveQueueToStorage(userId, queue);
    }
  }, [userId, queue]);

  // Update rate limit info every second
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      if (now >= rateLimitRef.current.resetTime) {
        rateLimitRef.current.count = 0;
        rateLimitRef.current.resetTime = now + 60000;
      }
      setRateLimitInfo({
        requestsThisMinute: rateLimitRef.current.count,
        resetTime: rateLimitRef.current.resetTime
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const addToQueue = useCallback(async (params: {
    prompt: string;
    model: string;
    numberOfImages: number;
    size: string;
    quality: string;
    style: string;
    background?: string;
    outputFormat?: string;
    outputCompression?: number;
    moderation?: string;
    generation_type?: 'single' | 'batch';
    metadata?: { abTestVariantId?: string };
    enableEnhancedPipeline?: boolean;
  }) => {
    if (!params.prompt || params.prompt.trim().length === 0) {
      console.error('❌ Cannot add to queue: empty prompt');
      toast.error('Please enter a prompt');
      return null;
    }

    if (!userId) {
      console.error('❌ Cannot add to queue: no user ID');
      toast.error('Please sign in to generate images');
      return null;
    }

    // Validate and truncate prompt
    const modelPromptLimit = getModelPromptLimit(params.model);
    let finalPrompt = params.prompt.trim();
    
    if (finalPrompt.length > modelPromptLimit) {
      finalPrompt = finalPrompt.substring(0, modelPromptLimit);
      console.warn(`⚠️ Prompt truncated from ${params.prompt.length} to ${finalPrompt.length} chars for model ${params.model}`);
      toast.warning(`Prompt was shortened to fit ${params.model} limits`);
    }

    // Get the actual batch size for this model from admin configuration
    const modelBatchSize = await getModelBatchSize(params.model);
    console.log('📊 Model batch size from admin config:', modelBatchSize, 'for model:', params.model);
    
    // Limit number of images to prevent overwhelming the system
    const maxImages = Math.min(params.numberOfImages, 100);
    if (params.numberOfImages > maxImages) {
      toast.warning(`Limited to ${maxImages} images to prevent rate limiting`);
    }

    // Create a single queue item for all requested images
    const queueId = crypto.randomUUID();
    
    const newItem: QueueItem = {
      id: queueId,
      prompt: finalPrompt,
      model: params.model,
      totalImages: maxImages, // Total number of images requested
      processedImages: 0,
      status: 'pending',
      generatedImages: [],
      size: params.size,
      quality: params.quality,
      style: params.style,
      background: params.background,
      outputFormat: params.outputFormat,
      outputCompression: params.outputCompression,
      moderation: params.moderation,
      createdAt: Date.now(),
      priority: 1,
      retryCount: 0,
      batchSize: modelBatchSize, // Use admin-configured batch size for internal processing
      userRequestedCount: maxImages, // Total images user requested
      lastDisplayedCount: 0,
      displayedBatches: 0,
      abTestVariantId: params.metadata?.abTestVariantId,
      enableEnhancedPipeline: params.enableEnhancedPipeline
    };

    // Add single item to queue
    setQueue(prev => [...prev, newItem]);
    console.log(`✅ Added single queue item with ${maxImages} total images, batch size: ${modelBatchSize}, model: ${params.model}`);
    
    toast.success(`Added ${maxImages} images to generation queue`);
    
    return queueId;
  }, [userId]);

  const processQueue = useCallback(async () => {
    if (processingRef.current || !userId) {
      console.log('⏸️ Processing skipped:', { processing: processingRef.current, userId: !!userId });
      return;
    }
    
    processingRef.current = true;
    setIsProcessing(true);

    try {
      if (processQueueTimeoutRef.current) {
        clearTimeout(processQueueTimeoutRef.current);
        processQueueTimeoutRef.current = null;
      }

      setQueue(currentQueue => {
        const nextItem = currentQueue.find(item => 
          item.status === 'pending' && 
          item.processedImages < item.userRequestedCount &&
          activeProcessingId.current !== item.id &&
          !pausedItems.current.has(item.id)
        );

        if (!nextItem) {
          console.log('ℹ️ No pending items found for processing');
          processingRef.current = false;
          setIsProcessing(false);
          activeProcessingId.current = null;
          return currentQueue;
        }

        const remainingImages = nextItem.userRequestedCount - nextItem.processedImages;
        
        if (remainingImages <= 0) {
          console.log(`✅ Item ${nextItem.id} already complete`);
          const updatedQueue = currentQueue.map(queueItem => 
            queueItem.id === nextItem.id 
              ? { ...queueItem, status: 'completed' as const, completedAt: Date.now() }
              : queueItem
          );
          processingRef.current = false;
          setIsProcessing(false);
          activeProcessingId.current = null;
          return updatedQueue;
        }

        activeProcessingId.current = nextItem.id;
        
        // Always use n=4 if user originally requested 4+ images, otherwise use their exact request
        const userOriginalRequest = nextItem.totalImages;
        const shouldUse4 = userOriginalRequest >= 4;
        const effectiveBatchSize = shouldUse4 ? Math.min(4, remainingImages) : Math.min(nextItem.userRequestedCount, remainingImages);
        console.log(`🔄 Processing item ${nextItem.id}: ${remainingImages} images remaining, user originally requested ${userOriginalRequest}, using n=${effectiveBatchSize}`);

        (async () => {
          try {
            // Enhanced rate limiting
            const now = Date.now();
            if (now >= rateLimitRef.current.resetTime) {
              rateLimitRef.current.count = 0;
              rateLimitRef.current.resetTime = now + 60000;
            }

            // Rate limit: 5 requests per minute to be safe
            if (rateLimitRef.current.count >= 5) {
              const waitTime = rateLimitRef.current.resetTime - now + 5000;
              console.log(`⏰ Rate limit reached (5/min), waiting ${Math.round(waitTime/1000)}s`);
              processQueueTimeoutRef.current = setTimeout(() => {
                processingRef.current = false;
                setIsProcessing(false);
                activeProcessingId.current = null;
                processQueue();
              }, waitTime);
              return;
            }

            // Minimum 12 seconds between requests
            const timeSinceLastRequest = now - lastRequestTime.current;
            if (timeSinceLastRequest < 12000) {
              const waitTime = 12000 - timeSinceLastRequest;
              console.log(`⏰ Spacing requests, waiting ${Math.round(waitTime/1000)}s`);
              processQueueTimeoutRef.current = setTimeout(() => {
                processingRef.current = false;
                setIsProcessing(false);
                activeProcessingId.current = null;
                processQueue();
              }, waitTime);
              return;
            }

            console.log(`📤 Using batch size: ${effectiveBatchSize} for model: ${nextItem.model}`);
            
            setQueue(prev => prev.map(queueItem => 
              queueItem.id === nextItem.id 
                ? { ...queueItem, status: 'processing' as const, startedAt: Date.now() }
                : queueItem
            ));

            // Final prompt validation
            const modelPromptLimit = getModelPromptLimit(nextItem.model);
            let validatedPrompt = nextItem.prompt;
            
            if (validatedPrompt.length > modelPromptLimit) {
              validatedPrompt = validatedPrompt.substring(0, modelPromptLimit);
              console.warn(`⚠️ Final prompt validation: truncated from ${nextItem.prompt.length} to ${validatedPrompt.length} chars`);
            }

            const requestPayload = {
              prompt: validatedPrompt,
              model: nextItem.model,
              n: effectiveBatchSize, // Use effective batch size (defaults to 4)
              size: nextItem.size,
              quality: nextItem.quality,
              style: nextItem.style,
              background: nextItem.background,
              output_format: nextItem.outputFormat,
              output_compression: nextItem.outputCompression,
              moderation: nextItem.moderation,
              user_id: userId,
              variant_id: nextItem.abTestVariantId,
              count: effectiveBatchSize,
              scope: 'dashboard',
              enableEnhancedPipeline: nextItem.enableEnhancedPipeline === true
            };

            console.log('📤 Sending parallel generation request:', {
              ...requestPayload,
              prompt: requestPayload.prompt.substring(0, 100) + '...',
              promptLength: requestPayload.prompt.length,
              batchSize: effectiveBatchSize
            });

            lastRequestTime.current = now;
            rateLimitRef.current.count++;
            
            // Get model-specific timeout
            const modelTimeout = getModelTimeout(nextItem.model);
            console.log(`⏱️ Using ${modelTimeout / 1000}s timeout for model: ${nextItem.model}`);
            
            // Use parallel generation for faster processing across multiple API keys
            const { data, error } = await supabase.functions.invoke('generate-parallel-images', {
              body: requestPayload
            });

            if (error) {
              const anyErr: any = error;
              const contextMsg = anyErr?.context?.error || anyErr?.context?.message;
              const finalMsg = contextMsg || anyErr?.message || 'Generation failed';
              console.error('❌ Parallel generation error:', anyErr, 'context:', anyErr?.context);
              
              // Provide more user-friendly error messages
              if (finalMsg.includes('No API keys available')) {
                toast.error('No API keys available. Please add API keys in Admin Panel > API Keys.', {
                  duration: 6000
                });
              } else if (/invalid[_\s-]*credentials|invalid authentication credentials|\b401\b|invalid_credentials/i.test(finalMsg)) {
                toast.error('Invalid API credentials. Please update API keys in Admin Panel > API Key Management.', {
                  duration: 7000
                });
              } else if (finalMsg.includes('Provider temporarily unavailable')) {
                toast.error('Image provider is temporarily unavailable. Please try again in a few moments or select a different model.', {
                  duration: 6000
                });
              } else if (/rate[- ]?limit|429|rate[- ]?limited/i.test(finalMsg)) {
                toast.error('Rate limit exceeded. Please wait and try again, or switch model.', {
                  duration: 6000
                });
              }
              
              throw new Error(finalMsg);
            }

            console.log('📥 Received parallel generation response:', {
              success: data?.success,
              imageCount: data?.images?.length || 0,
              totalGenerated: data?.totalGenerated,
              totalRequested: data?.totalRequested,
              parallelRequests: data?.parallelRequests,
              errors: data?.errors
            });

            if (!data?.success) {
              const errorMsg = data?.error || data?.details || 'No images returned from server';
              console.error('❌ Parallel generation returned error:', errorMsg);
              
              // Show user-friendly error based on error message
              if (errorMsg.includes('No API keys available')) {
                toast.error('No API keys available. Please add API keys in Admin Panel > API Keys.', {
                  duration: 6000
                });
              } else if (/invalid[_\s-]*credentials|invalid authentication credentials|\b401\b|invalid_credentials/i.test(String(errorMsg))) {
                toast.error('Invalid API credentials. Please update API keys in Admin Panel > API Key Management.', {
                  duration: 7000
                });
              } else if (errorMsg.includes('provider_unavailable') || errorMsg.includes('temporarily unavailable')) {
                toast.error('Image provider temporarily unavailable. Try again shortly or switch models.', {
                  duration: 6000
                });
              } else if (/rate[- ]?limit|429|rate[- ]?limited/i.test(errorMsg)) {
                toast.error('Rate limit exceeded. Please wait and try again, or switch models.', {
                  duration: 6000
                });
              }
              
              throw new Error(errorMsg);
            }

            if (!data?.images || data.images.length === 0) {
              console.error('❌ No images in parallel generation response');
              throw new Error('No images were generated');
            }

            const receivedImages = data.images || [];
            console.log(`✅ Generated ${receivedImages.length} images across ${data.parallelRequests} parallel requests for item ${nextItem.id}`);

            // Process images to ensure they have the correct format
            const dbIds: string[] = Array.isArray(data?.insertedImageIds) ? data.insertedImageIds : [];
            const processedImages = (receivedImages as any[]).map((img: any, idx: number) => {
              let imageUrl = '';
              
              // Handle different response formats
              if (img.b64_json) {
                // Convert base64 to data URL if needed
                imageUrl = img.b64_json.startsWith('data:') ? img.b64_json : `data:image/png;base64,${img.b64_json}`;
              } else if (img.url) {
                imageUrl = img.url;
              } else if (typeof img === 'string') {
                imageUrl = img.startsWith('data:') ? img : `data:image/png;base64,${img}`;
              }
              
              const imageData = {
                id: dbIds[idx] || undefined,
                url: imageUrl,
                b64_json: img.b64_json,
                revised_prompt: img.revised_prompt || nextItem.prompt
              };
              
              console.log(`📊 Processed image ${idx + 1}:`, {
                hasId: !!dbIds[idx],
                imageId: dbIds[idx],
                hasUrl: !!imageUrl,
                urlLength: imageUrl?.length
              });
              
              return imageData as any;
            });

            setQueue(prev => prev.map(queueItem => {
              if (queueItem.id === nextItem.id) {
                const updatedImages = [...queueItem.generatedImages, ...processedImages];
                const newProcessedCount = queueItem.processedImages + processedImages.length;
                const isCompleted = newProcessedCount >= queueItem.userRequestedCount;
                
                console.log(`📊 Updated item ${nextItem.id}: ${newProcessedCount}/${queueItem.userRequestedCount} completed`);
                
                return {
                  ...queueItem,
                  generatedImages: updatedImages,
                  processedImages: newProcessedCount,
                  status: isCompleted ? 'completed' as const : 'pending' as const,
                  completedAt: isCompleted ? Date.now() : undefined,
                  retryCount: 0,
                  lastDisplayedCount: newProcessedCount,
                  displayedBatches: queueItem.displayedBatches + 1
                };
              }
              return queueItem;
            }));

            const newProcessedCount = nextItem.processedImages + processedImages.length;
            const stillNeedImages = newProcessedCount < nextItem.userRequestedCount;
            
            activeProcessingId.current = null;
            processingRef.current = false;
            setIsProcessing(false);
            
            if (stillNeedImages) {
              console.log(`🔄 Still need ${nextItem.userRequestedCount - newProcessedCount} more images, scheduling next batch`);
              // Wait before next request
              processQueueTimeoutRef.current = setTimeout(() => processQueue(), 15000);
            } else {
              console.log(`🎉 Item ${nextItem.id} completed successfully`);
            }

            } catch (error) {
              console.error('❌ Image generation error for item', nextItem.id, ':', error);
              
              const maxRetries = 3;
              const newRetryCount = nextItem.retryCount + 1;
              
              // For rate limit errors, wait longer before retry
              const msg = (error as any)?.message || '';
              const isRateLimitError = /rate[- ]?limit|429|too many requests|rate[- ]?limited/i.test(msg);
              const isTimeoutError = /504|timeout|gateway timeout/i.test(msg);
              const isProviderUnavailable = /provider.*unavailable|temporarily unavailable/i.test(msg);
              const isInvalidCredError = /invalid[_\s-]*credentials|invalid authentication credentials|\b401\b|invalid_credentials/i.test(msg);
              
              // Do not retry on invalid credentials
              if (isInvalidCredError) {
                setQueue(prev => prev.map(queueItem => 
                  queueItem.id === nextItem.id 
                    ? { 
                        ...queueItem, 
                        status: 'error' as const, 
                        error: 'Invalid API credentials. Please update API keys in Admin Panel > API Key Management.',
                        completedAt: Date.now()
                      }
                    : queueItem
                ));
                toast.error('Invalid API credentials. Please update API keys in Admin Panel > API Key Management.', { duration: 7000 });
                activeProcessingId.current = null;
                processingRef.current = false;
                setIsProcessing(false);
                return;
              }
              
              if (newRetryCount < maxRetries) {
                console.log(`🔄 Retrying item ${nextItem.id} (${newRetryCount}/${maxRetries})`);
                setQueue(prev => prev.map(queueItem => 
                  queueItem.id === nextItem.id 
                    ? { ...queueItem, status: 'pending' as const, retryCount: newRetryCount }
                    : queueItem
                ));
                
                activeProcessingId.current = null;
                processingRef.current = false;
                setIsProcessing(false);
                
                // Wait longer for rate limit and provider unavailability errors
                const retryDelay = isRateLimitError ? 90000 : isProviderUnavailable ? 60000 : isTimeoutError ? 40000 : 20000;
                processQueueTimeoutRef.current = setTimeout(() => processQueue(), retryDelay);
              } else {
                console.error(`💀 Item ${nextItem.id} failed after ${maxRetries} attempts`);
                setQueue(prev => prev.map(queueItem => 
                  queueItem.id === nextItem.id 
                    ? { 
                        ...queueItem, 
                        status: 'error' as const, 
                        error: (error as any).message || 'Generation failed',
                        completedAt: Date.now()
                      }
                    : queueItem
                ));

                let userMessage = `Generation failed after ${maxRetries} attempts`;
                if ((error as any).message?.includes('too long')) {
                  userMessage = 'Prompt is too long for the selected model. Please shorten your prompt and try again.';
                } else if (isRateLimitError) {
                  userMessage = 'Rate limit exceeded. Please wait a few minutes before trying again.';
                } else if (isProviderUnavailable) {
                  userMessage = 'Image provider is currently unavailable. Please try again later or select a different model.';
                } else if ((error as any).message?.includes('Server error')) {
                  userMessage = 'Server error. Please check your prompt and try again.';
                }
                
                toast.error(userMessage, { duration: 6000 });
                
                activeProcessingId.current = null;
                processingRef.current = false;
                setIsProcessing(false);
              }
            }
        })();

        return currentQueue;
      });
    } catch (error) {
      console.error('❌ Error in processQueue:', error);
      activeProcessingId.current = null;
      processingRef.current = false;
      setIsProcessing(false);
    }
  }, [userId]);

  const queueLength = useMemo(() => queue.length, [queue]);
  
  const hasWorkToDo = useMemo(() => {
    return queue.some(item => 
      item.status === 'pending' && 
      item.processedImages < item.userRequestedCount &&
      !pausedItems.current.has(item.id)
    );
  }, [queue]);

  useEffect(() => {
    if (!processingRef.current && queueLength > 0 && hasWorkToDo) {
      const timer = setTimeout(() => {
        processQueue();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [queueLength, hasWorkToDo, processQueue]);

  useEffect(() => {
    return () => {
      if (processQueueTimeoutRef.current) {
        clearTimeout(processQueueTimeoutRef.current);
      }
      activeProcessingId.current = null;
    };
  }, []);

  const removeFromQueue = useCallback((itemId: string) => {
    console.log(`🗑️ Removing item ${itemId} from queue`);
    
    // Stop processing if this item is being processed
    if (activeProcessingId.current === itemId) {
      activeProcessingId.current = null;
      processingRef.current = false;
      setIsProcessing(false);
      if (processQueueTimeoutRef.current) {
        clearTimeout(processQueueTimeoutRef.current);
        processQueueTimeoutRef.current = null;
      }
    }
    
    // Remove from paused items
    pausedItems.current.delete(itemId);
    
    // Remove from queue
    setQueue(prev => {
      const newQueue = prev.filter(item => item.id !== itemId);
      console.log(`🗑️ Queue updated: ${prev.length} -> ${newQueue.length} items`);
      return newQueue;
    });
    
    toast.success('Request removed from queue');
  }, []);

  const clearQueue = useCallback(() => {
    console.log('🧹 Clearing entire queue');
    
    // Stop all processing
    if (processQueueTimeoutRef.current) {
      clearTimeout(processQueueTimeoutRef.current);
      processQueueTimeoutRef.current = null;
    }
    activeProcessingId.current = null;
    processingRef.current = false;
    setIsProcessing(false);
    pausedItems.current.clear();
    
    // Clear queue
    setQueue([]);
    
    // Clear from storage
    if (userId) {
      clearStorageQueue(userId);
    }
    
    toast.success('Queue cleared');
  }, [userId]);

  const resumeItem = useCallback((itemId: string) => {
    console.log(`▶️ Resuming item ${itemId}`);
    pausedItems.current.delete(itemId);
    setQueue(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, status: 'pending' as const, error: undefined }
        : item
    ));
    toast.success('Request resumed');
  }, []);

  const pauseItem = useCallback((itemId: string) => {
    console.log(`⏸️ Pausing item ${itemId}`);
    pausedItems.current.add(itemId);
    
    // If this item is currently being processed, stop it
    if (activeProcessingId.current === itemId) {
      activeProcessingId.current = null;
      processingRef.current = false;
      setIsProcessing(false);
      if (processQueueTimeoutRef.current) {
        clearTimeout(processQueueTimeoutRef.current);
        processQueueTimeoutRef.current = null;
      }
    }
    
    setQueue(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, status: 'paused' as const }
        : item
    ));
    toast.success('Request paused');
  }, []);

  return {
    queue,
    isProcessing,
    rateLimitInfo,
    addToQueue,
    removeFromQueue,
    clearQueue,
    resumeItem,
    pauseItem
  };
};
